import React, { useRef, useState } from 'react'
import { useDatePickerState } from 'react-stately'
import { DateValue, useButton, useDatePicker } from 'react-aria'
import { parseDate } from '@internationalized/date'
import styled from 'styled-components'
import { DateTime } from 'luxon'
import { FaCalendar } from 'react-icons/fa'
import { Calendar } from './Calendar'
import { RAPopover } from './RAPopover'
import { RADialog } from './RADialog'
import { DateField } from './RADateField'

const Container = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25em;

  > label {
    color: ${({ theme }) => theme.colors.font.regular};
    font-size: var(--small-text);
    font-weight: 700;
  }
`

const Group = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
`

const CalendarButton = styled.button`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: var(--default-text);
  font-weight: 700;
  background: none;
  border: none;
`

const CalendarIcon = styled(FaCalendar)`
  height: 28px;
  width: 28px;
`

const ErrorText = styled.span`
  color: ${({ theme }) => theme.colors.font.error};
  font-size: var(--small-text);
`

function getDate(date: string): string {
  const dateOnly = DateTime.fromISO(date).toISODate()
  if (dateOnly) {
    return dateOnly
  }
  throw new Error('Invalid date, expected ISO 8601 format')
}

interface Props {
  label?: string
  initialValue?: string
  onChange?: (date: string) => void
  minValue?: string
  maxValue?: string
  isDisabled?: boolean
  required?: boolean
  error?: string
}

export const DatePicker: React.FC<Props> = ({
  minValue,
  maxValue,
  initialValue,
  error,
  onChange,
  ...rest
}) => {
  const [value, setValue] = useState<string | undefined>(initialValue)
  const minDate = minValue ? parseDate(getDate(minValue)) : null
  const maxDate = maxValue ? parseDate(getDate(maxValue)) : null
  const dateValue = value ? parseDate(getDate(value)) : null
  const handleChange = (date: DateValue | null) => {
    if (date) {
      setValue(date.toString())
      onChange?.(date.toString())
    }
  }
  const stateProps = {
    ...rest,
    minValue: minDate,
    maxValue: maxDate,
    value: dateValue,
    onChange: handleChange,
  }
  const state = useDatePickerState({ ...stateProps })
  const ref = useRef(null)
  const {
    buttonProps,
    fieldProps,
    labelProps,
    groupProps,
    dialogProps,
    calendarProps,
  } = useDatePicker(stateProps, state, ref)

  const { buttonProps: ariaButtonProps } = useButton(buttonProps, ref)

  return (
    <Container>
      {rest.label ? <label {...labelProps}>{rest.label}</label> : null}
      <Group {...groupProps} ref={ref}>
        <DateField {...fieldProps} />
        <CalendarButton {...ariaButtonProps}>
          <CalendarIcon />
        </CalendarButton>
      </Group>
      {state.isInvalid && <ErrorText aria-hidden="true">{error}</ErrorText>}
      {state.isOpen && (
        <RAPopover state={state} triggerRef={ref} placement="bottom start">
          <RADialog {...dialogProps}>
            <Calendar {...calendarProps} firstDayOfWeek="sun" />
          </RADialog>
        </RAPopover>
      )}
    </Container>
  )
}
